import React, { useState, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  Alert,
  CircularProgress,
} from '@mui/material'
import FixTaskModal from '../commonUnit/FixTaskModal'
import { REASONING_GAP_TYPES, REASONING_GAP_DEFAULT_TASK } from '../reasoningGap/reasoningGapConfig'

const SESSION_TYPE_OPTIONS = [
  { value: 'Vizmental', label: 'Vizmental' },
  { value: 'HTML', label: 'GUI: HTML only' },
  { value: 'CSS', label: 'GUI: CSS only' },
  { value: 'JavaScript', label: 'GUI: JavaScript only' },
  { value: 'Mixed', label: 'GUI: Integrated HTML/CSS/JS' },
  { value: 'JSBin', label: 'GUI: JS Bin Style' },
  { value: 'InitialFinal', label: 'GUI: Initial/Final Comparison' },
  { value: 'InitialFinalCSS', label: 'GUI: Initial/Final Comparison (CSS)' },
  { value: 'InitialFinalJS', label: 'GUI: Initial/Final Comparison (JavaScript)' },
  { value: 'Pop Quiz', label: 'Pop Quiz' },
  ...Object.entries(REASONING_GAP_TYPES).map(([value, { label }]) => ({ value, label })),
]

// CRN-based course matching was already dead - courseService.js's CRN lookup is fully
// commented out, and SessionTable.jsx's student session list already treats a session's
// crn as optional (student accounts almost never have one set, so it falls back to
// sessions the student actually joined). The `session.crn` column is still NOT NULL /
// VARCHAR(10) in Postgres though (not something this app can migrate from here), so a
// short opaque placeholder is generated instead of asking the instructor for one -
// nothing reads it as a real course identifier anymore.
const generatePlaceholderCrn = () => Date.now().toString(36).slice(-10)

const AddSessionDialog = ({ open, setOpen, handleAddSession }) => {
  const [generateDescription, setGenerateDescription] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formError, setFormError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newSession, setNewSession] = useState({
    subject: '',
    task: '',
    type: '',
  })

  const isPopQuiz = newSession.type === 'Pop Quiz'
  const reasoningGapType = REASONING_GAP_TYPES[newSession.type]

  useEffect(() => {
    if (!isModalOpen && generateDescription !== '') {
      setNewSession(current => ({ ...current, task: generateDescription }))
    }
  }, [generateDescription, isModalOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    setNewSession({ ...newSession, [name]: value })
    setFormError('')
  }

  const handleAISuggestion = () => {
    setGenerateDescription(newSession.task)
    setIsModalOpen(true)
  }

  const handleCloseDialog = () => {
    setOpen(false)
    setFormError('')
    setNewSession({
      subject: '',
      task: '',
      type: '',
    })
  }

  const handleSubmit = async () => {
    if (!newSession.subject.trim() || !newSession.task.trim() || !newSession.type) {
      setFormError(`Complete the subject, type, and ${isPopQuiz ? 'quiz title' : 'task'} fields.`)
      return
    }

    setIsSubmitting(true)
    setFormError('')
    try {
      // Pop Quiz questions are authored on the session's own page after it's created
      // (see /session/:id), not here - this just reserves session.quiz with an empty
      // question list so that page has somewhere to save to.
      const quiz = isPopQuiz ? { questions: [] } : undefined
      const result = await handleAddSession({ ...newSession, crn: generatePlaceholderCrn(), quiz })

      if (!result) {
        setFormError('VizPI could not create the session. Check the backend console and try again.')
        return
      }
      handleCloseDialog()
    } catch (error) {
      setFormError(error.response?.data?.message || error.message || 'VizPI could not create the session.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Add New Session</DialogTitle>
      <DialogContent style={{ minHeight: '300px', paddingTop: '5px' }}>
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        <TextField
          autoFocus
          margin="dense"
          name="subject"
          label="Subject"
          type="text"
          fullWidth
          value={newSession.subject}
          onChange={handleChange}
        />
        <Autocomplete
          fullWidth
          options={SESSION_TYPE_OPTIONS}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={SESSION_TYPE_OPTIONS.find((option) => option.value === newSession.type) || null}
          onChange={(_e, newValue) => {
            // Reasoning Gap sessions carry their own built-in task (the CART quiz), so the
            // Task field only needs a label for the session list - prefill it if empty.
            setNewSession((current) => ({
              ...current,
              type: newValue ? newValue.value : '',
              task: newValue && REASONING_GAP_TYPES[newValue.value] && !current.task.trim()
                ? REASONING_GAP_DEFAULT_TASK
                : current.task,
            }))
            setFormError('')
          }}
          ListboxProps={{ style: { maxHeight: 220, overflow: 'auto' } }}
          renderInput={(params) => (
            <TextField {...params} margin="dense" label="Type" />
          )}
        />
        {reasoningGapType && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Creates a Reasoning Gap quiz session, with hints generated against the{' '}
            {reasoningGapType.condition === 'code' ? 'reference code' : 'reference reasoning'}. It starts on the CART
            quiz; open the session to choose the quiz (e.g. Neural Network Forward Pass) and review the task
            and examples. Create one session per study condition, pair them with "Random group link",
            then use Distribute to share the one shared link.
          </Alert>
        )}
        <TextField
          margin="dense"
          name="task"
          label={isPopQuiz ? 'Quiz Title' : reasoningGapType ? 'Session description' : 'Task'}
          type="text"
          fullWidth
          multiline={!isPopQuiz && !reasoningGapType}
          rows={isPopQuiz || reasoningGapType ? 1 : 6}
          value={newSession.task}
          onChange={handleChange}
          placeholder=""
        />
      </DialogContent>
      <DialogActions>
        {!isPopQuiz && !reasoningGapType && (
          <Button onClick={handleAISuggestion} color="primary">
            AI Assistant
          </Button>
        )}
        <Button onClick={handleCloseDialog} color="primary" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={20} /> : 'Add'}
        </Button>
      </DialogActions>
      {isModalOpen && (
        <FixTaskModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          taskContent={generateDescription}
          setTaskContent={setGenerateDescription}
        />
      )}
    </Dialog>
  )
}

export default AddSessionDialog
